import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Button } from '@cannect/new-components/atoms'
import {
  formatApiDate,
  getDocumentAlert,
  getPaymentAlert,
  isPauseEndingSoon,
  pauseEndsAt
} from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'

const copy = subscriptionsCopy.alerts
const DOC_LABEL = { prescription: 'Receita', anvisa: 'Autorização Anvisa' }

type AlertBannersProps = {
  detail: SubscriptionDetail
  onGoToDocuments: () => void
  cardAction?: ReactNode
  documentAction?: ReactNode
}

const Banner = ({ tone, children }: { tone: 'danger' | 'warning'; children: ReactNode }) => (
  <div
    role="alert"
    className={
      tone === 'danger'
        ? 'flex flex-col gap-3 rounded-xl border border-solid border-red-200 bg-red-50 p-4 text-sm text-red-900'
        : 'flex flex-col gap-3 rounded-xl border border-solid border-warning-400 bg-warning-100 p-4 text-sm text-neutral-800'
    }>
    {children}
  </div>
)

export const AlertBanners = ({ detail, onGoToDocuments, cardAction, documentAction }: AlertBannersProps) => {
  const pending = detail.documentsPending && (detail.status === 'active' || detail.status === 'paused')
  const pendingDocs = detail.pendingDocuments?.length ? detail.pendingDocuments : (['prescription'] as const)
  const endsAt = isPauseEndingSoon(detail) ? pauseEndsAt(detail) : null

  return (
    <div className="flex flex-col gap-3 empty:hidden">
      {pending && (
        <Banner tone="warning">
          <strong className="font-semibold">{copy.pendingTitle}</strong>
          <p className="m-0">
            {copy.pending(
              copy.pendingWhat([...pendingDocs]),
              detail.documentsDueAt ? formatApiDate(detail.documentsDueAt, 'dd/MM') : undefined
            )}
          </p>
          <ul className="m-0 list-disc pl-5">
            {pendingDocs.map((doc) => (
              <li key={doc}>{DOC_LABEL[doc]}</li>
            ))}
          </ul>
          <div>
            {detail.firstOrderId ? (
              <Button asChild size="md">
                <Link to={`/enviar-receita/${detail.firstOrderId}`}>{subscriptionsCopy.buttons.sendDocuments}</Link>
              </Button>
            ) : (
              <Button size="md" onClick={onGoToDocuments}>
                {subscriptionsCopy.buttons.sendDocuments}
              </Button>
            )}
          </div>
        </Banner>
      )}

      {getPaymentAlert(detail) && (
        <Banner tone="danger">
          <p className="m-0">{copy.payment}</p>
          {cardAction && <div>{cardAction}</div>}
        </Banner>
      )}

      {!pending && getDocumentAlert(detail) && (
        <Banner tone={detail.pauseReason === 'document' ? 'danger' : 'warning'}>
          <p className="m-0">{copy.documents}</p>
          <div className="flex flex-wrap gap-2">
            {documentAction}
            <Button variant="outline_light" size="md" onClick={onGoToDocuments}>
              {subscriptionsCopy.buttons.viewDocuments}
            </Button>
          </div>
        </Banner>
      )}

      {endsAt && (
        <Banner tone="warning">
          <p className="m-0">{subscriptionsCopy.pauseEnding(format(endsAt, 'dd/MM'))}</p>
        </Banner>
      )}
    </div>
  )
}
